import { useDebouncedValue } from '@saaga/shared/hooks/use-debounced-value.hook'
import { useCallback, useMemo, useState } from 'react'

import type { BillingUsageEntry, PaginationState } from '@/components/billing/billing.interface'
import type { DataTableFilterValue, SortOrder } from '@/components/data-table/data-table.interface'
import {
  type UsageFilterParams,
  useUsagePagination,
} from '@/hooks/billing/use-usage-pagination.hook'

export interface UseBalanceSettingsUsageReturn {
  paginatedUsageData: BillingUsageEntry[]
  processedTotalCount: number
  usageSearchValue: string
  setUsageSearchValue: (value: string) => void
  usageFilterValueList: DataTableFilterValue[]
  setUsageFilterValueList: (filterList: DataTableFilterValue[]) => void
  usageSortBy: string
  usageSortOrder: SortOrder
  handleUsageSortChange: (sortBy: string, sortOrder: SortOrder) => void
  usagePagination: PaginationState
  handleUsagePageChange: (page: number) => void
  handleUsagePageSizeChange: (pageSize: number) => void
  isLoading: boolean
  error: string | null
  refetch: () => void
}

const USAGE_TYPE_TO_STEP_TYPE: Record<string, string> = {
  llm: 'llm_call',
  tool: 'tool_call',
  other: 'other',
}

const mapFilterToStepTypes = (filterList: DataTableFilterValue[]): string[] | undefined => {
  const typeFilter = filterList.find((f) => f.filterId === 'type')
  if (!typeFilter || typeFilter.valueList.length === 0) return undefined
  return typeFilter.valueList.map((v) => USAGE_TYPE_TO_STEP_TYPE[v] ?? v)
}

const SEARCH_DEBOUNCE_MS = 300

const useUsageFilters = () => {
  const [usageSearchValue, setUsageSearchValue] = useState('')
  const [usageFilterValueList, setUsageFilterValueList] = useState<DataTableFilterValue[]>([])
  const [usageSortBy, setUsageSortBy] = useState<string>('createdAt')
  const [usageSortOrder, setUsageSortOrder] = useState<SortOrder>('desc')
  const handleUsageSortChange = useCallback((sortBy: string, order: SortOrder): void => {
    setUsageSortBy(sortBy)
    setUsageSortOrder(order)
  }, [])
  return {
    usageSearchValue,
    setUsageSearchValue,
    usageFilterValueList,
    setUsageFilterValueList,
    usageSortBy,
    usageSortOrder,
    handleUsageSortChange,
  }
}

export const useBalanceSettingsUsage = (): UseBalanceSettingsUsageReturn => {
  const filters = useUsageFilters()
  const debouncedSearch = useDebouncedValue(filters.usageSearchValue, SEARCH_DEBOUNCE_MS)

  const serverFilters: UsageFilterParams = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      stepType: mapFilterToStepTypes(filters.usageFilterValueList),
      sortBy: filters.usageSortBy,
      sortOrder: filters.usageSortOrder,
    }),
    [debouncedSearch, filters.usageFilterValueList, filters.usageSortBy, filters.usageSortOrder]
  )

  const paginated = useUsagePagination(25, serverFilters)

  const usagePagination: PaginationState = useMemo(
    () => ({
      page: paginated.currentPage,
      pageSize: paginated.pageSize,
      totalCount: paginated.totalCount,
    }),
    [paginated.currentPage, paginated.pageSize, paginated.totalCount]
  )

  return {
    paginatedUsageData: paginated.data,
    processedTotalCount: paginated.totalCount,
    ...filters,
    usagePagination,
    handleUsagePageChange: paginated.handlePageChange,
    handleUsagePageSizeChange: paginated.handlePageSizeChange,
    isLoading: paginated.isLoading,
    error: paginated.error,
    refetch: paginated.refetch,
  }
}
